import { Stack, Typography } from '@mui/material';
import { FilterKeys, FilterValue } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useSubjects } from '@schooly/hooks/use-subjects';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectOptionsArchivedEmptyStub } from '../SelectOptionsArchivedEmptyStub';
import { SelectOptionsArchivedIcon } from '../SelectOptionsArchivedIcon';
import { SelectSearchInput } from '../SelectSearchInput';
import { renderSubjectTags } from './SubjectExpandedSelect';

type SubjectSelectMultipleProps = {
  schoolId: string;
  selectedIds: string[];
  onSelectSubjectId: (v: string) => void;
  optionsTitle?: string;
  isAdmin?: boolean;
  allowArchived?: boolean;
  availableIds?: FilterValue[];
  required?: boolean;
  renderAdditionalSelectedItems?: (opened: boolean) => React.ReactNode;
  renderAdditionalContent?: () => React.ReactNode;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;

export const SubjectSelectMultiple: FC<SubjectSelectMultipleProps> = ({
  schoolId,
  selectedIds,
  onSelectSubjectId,
  allowArchived,
  placeholder,
  optionsTitle,
  onClear,
  isAdmin,
  availableIds,
  required,
  renderAdditionalSelectedItems,
  renderAdditionalContent,
  ...dropdownProps
}) => {
  const { getConfirmation } = useConfirmationDialog();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdown = useRef<DropdownSelect | null>(null);
  const [query, setQuery] = useState('');

  const { subjects, activeSubjects, archivedSubjects, isLoading } = useSubjects(
    { schoolId },
    { refetchOnMount: 'always' },
  );

  const isDisabled = useCallback(
    (id: string) => !!required && selectedIds.length === 1 && selectedIds.includes(id),
    [required, selectedIds],
  );

  const showAllArchivedRightIcon =
    !allowArchived && !selectedIds.length && !activeSubjects.length && archivedSubjects.length > 0;
  const showAllArchivedEmptyStub = !allowArchived && !!selectedIds.length && !activeSubjects.length;
  const showConfirmationBeforeClear =
    onClear &&
    !allowArchived &&
    archivedSubjects.find((subject) => selectedIds.includes(subject.id));

  const handleSelectSubjectId = useCallback(
    async (id: string) => {
      const subject = subjects.find((prop) => prop.id === id);

      if (
        !allowArchived &&
        subject?.archived &&
        !(await getConfirmation({
          textId: 'deselect-subject-archived',
          textValues: { name: subject.name },
        }))
      ) {
        return;
      }
      onSelectSubjectId(id);
    },
    [allowArchived, getConfirmation, onSelectSubjectId, subjects],
  );

  const onClearArchivedSubjects = useCallback(async () => {
    if (onClear) {
      const isConfirmed = await getConfirmation({
        textId: 'deselect-subjects-archived',
      });
      if (isConfirmed) {
        onClear();
      }
    }
  }, [getConfirmation, onClear]);

  const renderAllArchivedIcon = useCallback(
    () => <SelectOptionsArchivedIcon isAdmin={!!isAdmin} type={FilterKeys.Subject} />,
    [isAdmin],
  );

  const allArchivedEmptyStub = useMemo(() => {
    return <SelectOptionsArchivedEmptyStub isAdmin={!!isAdmin} type={FilterKeys.Subject} />;
  }, [isAdmin]);

  const renderContent = useCallback(() => {
    if (isLoading) return <SelectContentSkeleton />;

    const q = query.trim().toLowerCase();
    const filteredActiveSubjects = activeSubjects.filter((s) => {
      const isAvailable = !availableIds || availableIds.includes(s.id);
      return s.name.toLowerCase().includes(q) && isAvailable;
    });
    const filteredArchivedSubjects = allowArchived
      ? archivedSubjects.filter((s) => {
          const isAvailable = !availableIds || availableIds.includes(s.id);
          return s.name.toLowerCase().includes(q) && isAvailable;
        })
      : [];

    if (
      !filteredActiveSubjects.length &&
      !filteredArchivedSubjects.length &&
      !showAllArchivedEmptyStub &&
      !renderAdditionalContent
    ) {
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );
    }

    return (
      <Stack gap={1} m={1}>
        {renderAdditionalContent?.()}
        {optionsTitle && <Typography variant="h4">{optionsTitle}</Typography>}
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {showAllArchivedEmptyStub && allArchivedEmptyStub}
          {renderSubjectTags({
            ids: filteredActiveSubjects.map((s) => s.id),
            tagProps: (id) => ({
              schoolId,
              disabled: isDisabled(id),
              variant: selectedIds?.includes(id) ? 'filled' : undefined,
            }),
            onClick: (id: string) => {
              onSelectSubjectId(id);
              setQuery('');
            },
          })}
        </Stack>

        {allowArchived && filteredArchivedSubjects.length > 0 && (
          <>
            <Typography variant="h4" mt={1} ml={1}>
              <FormattedMessage id="filter-Archived" />
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {renderSubjectTags({
                ids: filteredArchivedSubjects.map((s) => s.id),
                tagProps: (id) => ({
                  schoolId,
                  disabled: isDisabled(id),
                  variant: selectedIds?.includes(id) ? 'filled' : undefined,
                }),
                onClick: (id: string) => {
                  onSelectSubjectId(id);
                  setQuery('');
                },
              })}
            </Stack>
          </>
        )}
      </Stack>
    );
  }, [
    allowArchived,
    archivedSubjects,
    allArchivedEmptyStub,
    activeSubjects,
    availableIds,
    isDisabled,
    isLoading,
    onSelectSubjectId,
    optionsTitle,
    query,
    schoolId,
    selectedIds,
    showAllArchivedEmptyStub,
    renderAdditionalContent,
  ]);

  return (
    <DropdownSelect
      {...dropdownProps}
      renderRightIcon={showAllArchivedRightIcon ? renderAllArchivedIcon : undefined}
      onClear={showConfirmationBeforeClear ? onClearArchivedSubjects : onClear}
      ref={dropdown}
      placeholder={placeholder}
      hasValues={selectedIds.length > 0 || dropdownProps.hasValues}
      renderContent={renderContent}
      onClickInputArea={() => inputRef.current?.focus()}
    >
      {(opened) => (
        <>
          {renderAdditionalSelectedItems?.(opened)}
          {renderSubjectTags({
            ids: selectedIds,
            tagProps: (id) => {
              const disabled = isDisabled(id);
              return {
                schoolId,
                size: placeholder ? 'small' : undefined,
                sx: { maxWidth: 200 },
                disabled,
                ...(disabled ? { onDelete: undefined } : {}),
              };
            },
            onDelete: opened ? handleSelectSubjectId : undefined,
          })}
          {opened && (
            <SelectSearchInput
              ref={inputRef}
              autoFocus
              value={query || ''}
              onChangeText={setQuery}
            />
          )}
        </>
      )}
    </DropdownSelect>
  );
};
